// Tenant brand-kit -> jrni-tokens CSS-variable bridge.
//
// jrni-tokens/jrni-ui are CD-owned and byte-locked (never edited here). But
// every jrni-ui primitive (Button, Input, Card, Dropdown, Menu, IconRail,
// ...) reads its colors through `var(--jrni-color-*, <default-hex>)` CSS
// custom properties resolved at `:root` (see packages/jrni-tokens/src/styles/
// tokens.css + packages/shell/src/ThemeProvider.tsx). This module is a
// *runtime* mechanism, entirely inside the platform harness: it fetches the
// tenant's live brand kit (GET /v1/branding/theme, already-derived +
// WCAG-checked by workers/events/src/canvas-brand-derivation.ts) and sets
// those same custom properties as INLINE styles on <html>. An inline style
// on the root element has higher cascade specificity than any selector-based
// rule (:root {...}, [data-tenant="cns"] {...}), so this cleanly overrides
// the JRNI defaults for every jrni-ui component -- without touching a single
// locked byte in jrni-tokens or jrni-ui.
//
// Absent/failed brand-kit fetch -> no overrides are set -> JRNI defaults
// stand untouched. Fully tenant-agnostic: nothing here names a tenant.
//
// KNOWN LIMIT (documented, not silently swallowed): jrni-ui components that
// consume `var(--jrni-color-*)` pick this up automatically. Claude Design's
// own screen-level styling in `cd-app/screens/**` frequently hardcodes the
// JRNI purple as a literal hex in an inline `style` prop instead of reading
// the CSS variable -- those specific hardcoded elements are NOT reachable by
// this bridge (see docket in the PR description / commit body for the exact
// count). That gap is a CD-authored-styling issue, tracked as a follow-up
// ask to CD, not something this runtime bridge can fix without touching
// CD's screens.
import { fetchTenantTheme } from "@v0y4ge/api-client";

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const int = Number.parseInt(m[1]!, 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

/** Mix a hex color toward white by `t` (0..1) -- generic tint-ladder helper.
 *  Tenant-agnostic: works for any brand hex, not tuned per tenant. */
function tint(hex: string, t: number): string | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb;
  const mix = (c: number) => Math.round(c + (255 - c) * t);
  return `#${[mix(r), mix(g), mix(b)].map((c) => c.toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}

function rgba(hex: string, alpha: number): string | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const SYSTEM_SAFE_FONTS = new Set(["Helvetica Neue", "Arial", "Georgia"]);
const _loadedFonts = new Set<string>();

/** Load a Google Fonts stylesheet for a brand-kit heading/body font. No-op
 *  for system-safe fallbacks (no network fetch needed) and repeat calls.
 *  Mirrors the pattern already used for public landing pages
 *  (cd-app/screens/hubs/PublicHubs.tsx ensureLandingFont). */
function ensureBrandFont(family: string | undefined | null): void {
  if (!family || SYSTEM_SAFE_FONTS.has(family) || _loadedFonts.has(family)) return;
  _loadedFonts.add(family);
  const id = `tenant-brand-font-${family.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, "+")}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

function setVar(name: string, value: string | null | undefined): void {
  if (!value) return;
  document.documentElement.style.setProperty(name, value);
}

/** The design system's own value for a token, read from the live stylesheet
 *  so nothing here restates a jrni-tokens hex. An inline override already on
 *  <html> is lifted for the read: the bridge runs once per boot today, but a
 *  second run must not mistake its own earlier write for the default. */
function designSystemDefault(name: string): string {
  const root = document.documentElement;
  const inline = root.style.getPropertyValue(name);
  if (inline) root.style.removeProperty(name);
  const value = getComputedStyle(root).getPropertyValue(name).trim();
  if (inline) root.style.setProperty(name, inline);
  return value;
}

function sameColor(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
}

export interface TenantThemeColors {
  brandColor: string;
  /** The accent the tenant stored. Null when it never chose one -- the
   *  server then substitutes a platform fallback, which is not the tenant's
   *  intent and must not displace the design system's own focus ring. */
  accentColor: string | null | undefined;
  coverColor: string;
  link?: string | null;
}

export interface DesignSystemDefaults {
  primary: string;
  coverBg: string;
}

/**
 * The CSS-variable overrides a tenant brand needs -- and ONLY those.
 *
 * The white-mix ladder below is a generic approximation for an arbitrary
 * brand hex. The design system's own ramp is hand-tuned, so a tenant whose
 * colour IS the design-system default must get no override at all: mixing
 * the default toward white yields a visibly different hover/tint ramp than
 * the one the design system ships, and the tenant then renders unlike the
 * design it was composed from. Each family is therefore skipped when its
 * source colour equals the design-system default.
 *
 * KNOWN LIMIT: `defaults` is read under the active theme. Under
 * `[data-theme="dark"]` the design system re-tunes primary-1, so a
 * default-brand tenant compares unequal there and is overridden as before.
 * Studio pins light today (cd-app/App.tsx), so this is latent.
 */
export function planTenantThemeVars(
  colors: TenantThemeColors,
  defaults: DesignSystemDefaults,
): Record<string, string> {
  const vars: Record<string, string> = {};
  const put = (name: string, value: string | null | undefined) => {
    if (value) vars[name] = value;
  };
  const primary = colors.brandColor;
  const accent = colors.accentColor;

  // --- Primary color family (brandColor -> --jrni-color-primary-*) ---
  if (!sameColor(primary, defaults.primary)) {
    const p2 = tint(primary, 0.60);
    const p3 = tint(primary, 0.78);
    const p4 = tint(primary, 0.90);
    put("--jrni-color-primary-1", primary);
    put("--jrni-color-primary-2", p2);
    put("--jrni-color-primary-3", p3);
    put("--jrni-color-primary-4", p4);

    put("--jrni-color-brand-primary", primary);
    put("--jrni-color-brand-primary-hover", p2);
    put("--jrni-color-brand-primary-muted", p3);

    put("--jrni-color-fill-primary-1", primary);
    put("--jrni-color-fill-primary-2", p3);
    put("--jrni-color-fill-primary-3", p4);

    // AA-checked darker variant already computed server-side (canvas-brand-
    // derivation's toneAdjustedForContrast) -- reuse it instead of guessing.
    put("--jrni-color-primary-text", colors.link ?? primary);

    put("--jrni-color-brand-glow", rgba(primary, 0.30));
    put("--jrni-color-brand-glow-strong", rgba(primary, 0.35));
  }

  // --- Accent color (accentColor -> focus ring / cover accent) ---
  // Applied only for an accent the tenant really has: none stored, or one
  // equal to the design-system primary, is "no accent of its own". The design
  // system binds its focus ring to that primary for contrast and hand-picks
  // its cover accent, so both are left standing rather than approximated.
  if (accent && !sameColor(accent, defaults.primary)) {
    put("--jrni-color-utility-focus", tint(accent, 0.55));
    put("--jrni-color-cover-accent", tint(accent, 0.35));
  }

  // --- Cover / dark surfaces (coverColor -> PDF/onboarding splash bg) ---
  if (!sameColor(colors.coverColor, defaults.coverBg)) {
    put("--jrni-color-cover-bg", colors.coverColor);
  }

  return vars;
}

/** Race a promise against a timeout so a slow/unreachable branding endpoint
 *  can never hang app boot -- resolves to null on timeout, letting JRNI
 *  defaults stand. */
function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);
    p.then((v) => { clearTimeout(timer); resolve(v); })
      .catch(() => { clearTimeout(timer); resolve(null); });
  });
}

/**
 * Fetch the tenant's brand kit and override every jrni-tokens CSS variable
 * this runtime mechanism can reach. Fail-open: any error (network, parse,
 * missing branding row) leaves JRNI defaults in place -- this must never
 * block or break app boot.
 */
export async function applyTenantThemeBridge(): Promise<void> {
  // 4s: a Cloudflare edge/worker cold path (D1 read + first-hit warmup right
  // after a deploy) can occasionally exceed 1.5s -- verified live against
  // Live-tenant regression: the very first request after a fresh Pages deploy raced past a
  // 1.5s budget and left JRNI defaults standing, while every subsequent
  // (warm) request resolved in ~400ms. 4s covers the cold case without
  // meaningfully delaying first paint on the warm path that's the norm.
  const theme = await withTimeout(fetchTenantTheme(), 4000);
  if (!theme) return;

  const { brand, derived } = theme;
  const vars = planTenantThemeVars(
    {
      brandColor: brand.brandColor,
      accentColor: brand.accentColor,
      coverColor: brand.coverColor,
      link: derived.link,
    },
    {
      primary: designSystemDefault("--jrni-color-primary-1"),
      coverBg: designSystemDefault("--jrni-color-cover-bg"),
    },
  );
  for (const [name, value] of Object.entries(vars)) setVar(name, value);

  // --- Fonts (headingFont/bodyFont -> the single --jrni-font-family-sans
  // token jrni-tokens exposes today; see docket -- there is currently no
  // separate heading-vs-body font token to bridge into). ---
  const bodyFont = brand.bodyFont;
  if (bodyFont) {
    ensureBrandFont(bodyFont);
    setVar(
      "--jrni-font-family-sans",
      `'${bodyFont}', 'Helvetica Neue', system-ui, -apple-system, 'Segoe UI', Arial, sans-serif`,
    );
  }
  if (brand.headingFont && brand.headingFont !== bodyFont) {
    ensureBrandFont(brand.headingFont);
  }
}
